import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Tooltip, cn } from '../ui';
import type { LyricsSettings } from './lyricsSettings';

export interface BhajanSettingsMenuProps {
  settings: LyricsSettings;
  onChange: (next: LyricsSettings) => void;
  onCopyAsText?: () => void;
}

const SettingsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </svg>
);

const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

export const BhajanSettingsMenu: React.FC<BhajanSettingsMenuProps> = ({ settings, onChange, onCopyAsText }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const update = (patch: Partial<LyricsSettings>) => {
    const next = { ...settings, ...patch };
    // If svaras get hidden or repeatPallavi turns off, reset interpolate (per user spec).
    if (!next.showSvaras || !next.repeatPallavi) next.interpolateSvaras = false;
    onChange(next);
  };

  return (
    <div className="relative" ref={ref}>
      <Tooltip content="Bhajan settings" side="bottom">
        <IconButton
          ariaLabel="Bhajan settings"
          variant="ghost"
          icon={<SettingsIcon />}
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
        />
      </Tooltip>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-64 rounded-xl border border-light shadow-3 z-[850] overflow-hidden"
          style={{ background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur)' }}
        >
          <div className="p-2">
            <ToggleRow
              label="Show svaras"
              hint="Carnatic notation above lyrics"
              checked={settings.showSvaras}
              onChange={(v) => update({ showSvaras: v })}
            />
            <ToggleRow
              label="Repeat pallavi"
              hint="Show the pallavi after every caranam"
              checked={settings.repeatPallavi}
              onChange={(v) => update({ repeatPallavi: v })}
            />
            {settings.repeatPallavi && settings.showSvaras && (
              <ToggleRow
                label="Fill missing svaras for caranams"
                hint="Copy the first caranam's svaras into caranams that don't have them"
                checked={settings.interpolateSvaras}
                onChange={(v) => update({ interpolateSvaras: v })}
                indented
              />
            )}
            {onCopyAsText && (
              <>
                <div className="my-1 h-px bg-light/60" aria-hidden />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { onCopyAsText(); setOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-small text-text-primary hover:bg-bg-3 transition-colors"
                >
                  <span className="text-text-tertiary"><CopyIcon /></span>
                  <span>Copy lyrics as text</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ToggleRow: React.FC<{
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  indented?: boolean;
}> = ({ label, hint, checked, onChange, indented }) => (
  <label
    className={cn(
      'flex items-start gap-3 px-3 py-2 rounded-md hover:bg-bg-3 transition-colors cursor-pointer',
      indented && 'pl-7'
    )}
  >
    <input
      type="checkbox"
      role="switch"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 accent-accent-gold w-4 h-4 shrink-0"
    />
    <span className="flex-1 min-w-0">
      <span className="block text-small text-text-primary">{label}</span>
      {hint && <span className="block text-[11px] text-text-tertiary mt-0.5">{hint}</span>}
    </span>
  </label>
);
